import {
  CLABE_LENGTH,
  CLABE_PROVIDER_CODE,
  CLABE_STATUS,
  PHASE4_ERROR_CODES,
} from 'constants/digitalLoanPhase4';

/** Lee el error normalizado de ApiFetch. */
export function readPhase4Error(error) {
  const statusCode = Number(error?.statusCode) || null;
  const code = typeof error?.data?.error === 'string'
    ? error.data.error
    : null;
  return {
    statusCode,
    code,
    pendingUntil: error?.rawData?.pendingUntil || null,
    operationalWindowOk: error?.rawData?.operationalWindowOk,
  };
}

/**
 * Destino de un error de Fase 4 fuera de la validación de CLABE.
 * 503 de CLABE se clasifica en classifyClabeOutcome.
 */
export function routePhase4Error(error) {
  const parsed = readPhase4Error(error);
  switch (parsed.code) {
    case PHASE4_ERROR_CODES.OFFER_EXPIRED:
    case PHASE4_ERROR_CODES.OFFER_AMOUNT_MISMATCH:
    case PHASE4_ERROR_CODES.OFFER_TERM_MISMATCH:
      return { type: 'refetch-offer', ...parsed };
    case PHASE4_ERROR_CODES.CLABE_NOT_VALIDATED:
      return { type: 'go-clabe', ...parsed };
    case PHASE4_ERROR_CODES.BIOMETRIC_FINAL_REQUIRED:
      return { type: 'go-biometric', ...parsed };
    case PHASE4_ERROR_CODES.CONTRACT_ALREADY_GENERATED:
      return { type: 'go-ready', ...parsed };
    case PHASE4_ERROR_CODES.CONTRACT_GENERATION_FAILED:
      return { type: 'contract-retry', ...parsed };
    case PHASE4_ERROR_CODES.CONTRACT_NOT_GENERATED:
      if (parsed.operationalWindowOk === false) {
        return { type: 'go-waiting', ...parsed };
      }
      return { type: 'contract-retry', ...parsed };
    case PHASE4_ERROR_CODES.DOCUMENT_TYPE_INVALID:
      return { type: 'document-error', ...parsed };
    case PHASE4_ERROR_CODES.PHASE_3_NOT_CLOSED:
      return { type: 'go-phase3', ...parsed };
    default:
      return { type: 'modal', ...parsed };
  }
}

/** Solo dígitos, recortado a 18. No persiste el valor. */
export function sanitizeClabeInput(value) {
  return String(value || '').replace(/\D/g, '').slice(0, CLABE_LENGTH);
}

export function isClabeReady(value) {
  return /^\d{18}$/.test(String(value || ''));
}

/** 0 es válido. Un valor ausente no se trata como código 0. */
export function readProviderCode(value) {
  if (value === null || value === undefined || value === '') return null;
  const code = Number(value);
  if (!Number.isInteger(code)) return null;
  return code;
}

const PROVIDER_OUTCOMES = Object.freeze({
  [CLABE_PROVIDER_CODE.VALID_NAME_MATCH]: {
    type: 'validated',
    reason: 'name-match',
  },
  [CLABE_PROVIDER_CODE.VALID_NAME_UNAVAILABLE]: {
    type: 'validated',
    reason: 'name-unavailable',
  },
  [CLABE_PROVIDER_CODE.ACCOUNT_NOT_FOUND]: {
    type: 'clabe-invalid',
    reason: 'account',
  },
  [CLABE_PROVIDER_CODE.NAME_MISMATCH]: {
    type: 'clabe-invalid',
    reason: 'name-mismatch',
  },
  [CLABE_PROVIDER_CODE.DEPOSIT_RETURNED]: {
    type: 'clabe-invalid',
    reason: 'returned',
  },
  [CLABE_PROVIDER_CODE.UNAVAILABLE]: {
    type: 'clabe-retryable',
    reason: 'unavailable',
  },
});

/** Clave de literal para el resultado de CLABE. */
export function clabeFeedbackKey(outcome) {
  if (outcome?.reason === 'name-mismatch') return 'nameMismatch';
  if (outcome?.reason === 'account') return 'invalidAccount';
  if (outcome?.reason === 'returned') return 'returned';
  if (outcome?.type === 'pending') return 'pending';
  if (outcome?.type === 'clabe-retryable') return 'unavailable';
  return 'invalid';
}

/**
 * El código del proveedor gana sobre el status.
 * PENDING sin código no bloquea: el banco todavía no responde.
 * 503 o UNAVAILABLE es reintento, no CLABE inválida.
 */
export function classifyClabeOutcome({ error, response } = {}) {
  if (!error && response) {
    const providerCode = readProviderCode(response.providerCode);
    const mapped = providerCode == null ? null : PROVIDER_OUTCOMES[providerCode];
    if (mapped) {
      return { ...mapped, providerCode };
    }
    if (
      response.status === CLABE_STATUS.PENDING
      && providerCode == null
    ) {
      return { type: 'pending', providerCode: null };
    }
    if (response.status === CLABE_STATUS.VALIDATED) {
      return { type: 'validated' };
    }
    if (response.status === CLABE_STATUS.REJECTED) {
      return { type: 'clabe-invalid', reason: 'invalid' };
    }
    if (response.status === CLABE_STATUS.UNAVAILABLE) {
      return { type: 'clabe-retryable', reason: 'unavailable' };
    }
  }

  const parsed = readPhase4Error(error);
  if (
    parsed.statusCode === 503
    || parsed.code === PHASE4_ERROR_CODES.UNAVAILABLE
  ) {
    return { type: 'clabe-retryable', ...parsed };
  }
  if (
    parsed.code === PHASE4_ERROR_CODES.CLABE_INVALID
    || parsed.code === PHASE4_ERROR_CODES.REJECTED
    || parsed.code === CLABE_STATUS.REJECTED
  ) {
    return { type: 'clabe-invalid', ...parsed };
  }
  if (parsed.code === PHASE4_ERROR_CODES.CLABE_NOT_VALIDATED) {
    return { type: 'go-clabe', ...parsed };
  }
  if (parsed.code === PHASE4_ERROR_CODES.PHASE_3_NOT_CLOSED) {
    return { type: 'go-phase3', ...parsed };
  }
  return { type: 'modal', ...parsed };
}

/**
 * HTTP 200 tiene dos éxitos: contrato listo, o pendiente con fecha.
 * El pendiente no es un error.
 */
export function resolveContractGenerationOutcome(response) {
  if (response?.generated === true) {
    return { type: 'ready', pendingUntil: null };
  }
  const pendingUntil = response?.pendingUntil || null;
  if (response?.generated === false && pendingUntil) {
    return { type: 'waiting', pendingUntil };
  }
  if (
    response?.generated === false
    && response?.operationalWindowOk === false
  ) {
    return { type: 'waiting', pendingUntil };
  }
  return { type: 'unknown', pendingUntil };
}

/** Fecha y hora locales para el cliente. No calcula vigencia. */
export function formatPhase4DateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Huella real: boolean passed y string no vacío.
 * Cualquier otra forma se rechaza. No se guarda.
 */
export function isRealFingerprintCapture(capture) {
  return Boolean(
    capture
    && typeof capture.passed === 'boolean'
    && typeof capture.fingerprint === 'string'
    && capture.fingerprint.length > 0,
  );
}
